import type { ComponentType, ReactNode } from "react";
import { Inbox, SearchX, PlugZap, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Nothing to show, said plainly, with the one next step.
 * - "empty": nothing recorded yet ("No invoices yet" + how to add one).
 * - "filtered": records exist but none match ("Clear filters").
 * - "not-connected": a source is not connected; never pretend there is data.
 */
export function EmptyState({
  title,
  children,
  action,
  variant = "empty",
  icon,
  compact = false,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  variant?: "empty" | "filtered" | "not-connected";
  icon?: ComponentType<{ size?: number }>;
  /** Smaller padding, for use inside a card, table or drawer. */
  compact?: boolean;
}) {
  const Icon = icon ?? (variant === "filtered" ? SearchX : variant === "not-connected" ? PlugZap : Inbox);
  return (
    <div className={`empty empty-${variant}${compact ? " compact" : ""}`}>
      <div className="empty-icon" aria-hidden="true">
        <Icon size={compact ? 20 : 25} />
      </div>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/**
 * A fetch or save that failed and has nothing earlier to show. (When an
 * earlier result is still on screen, DataState shows the "Showing earlier
 * data" line instead.)
 */
export function ErrorState({
  title = "This did not load",
  message,
  onRetry,
  compact = false,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
  compact?: boolean;
}) {
  return (
    <div className={`empty empty-error${compact ? " compact" : ""}`} role="alert">
      <div className="empty-icon" aria-hidden="true">
        <AlertTriangle size={compact ? 20 : 25} />
      </div>
      <h3>{title}</h3>
      <p>{message}</p>
      {onRetry && (
        <button type="button" className="button secondary small" onClick={onRetry}>
          <RefreshCw size={14} /> Try again
        </button>
      )}
    </div>
  );
}
